
import { ELECTRONIC_BRANDS, SMARTPHONE_MODELS, TABLET_MODELS, COMPUTER_BRANDS, PRINTER_BRANDS, GAMING_BRANDS, AV_BRANDS, CONSOLE_MODELS, WATCH_BRANDS, SMARTWATCH_MODELS } from './brands/electronics';
import { VEHICLE_BRANDS, CAR_MODELS, MOTORCYCLE_BRANDS, MOTORCYCLE_MODELS } from './brands/vehicles';
import { HOME_APPLIANCE_BRANDS, FASHION_BRANDS, SPORTS_BRANDS, CLOTHING_SIZES_INT, CLOTHING_SIZES_NUM, SHOE_SIZES_EUR, FASHION_MATERIALS, FASHION_PATTERNS, FASHION_STYLES } from './brands/fashion';
import {
    INSTRUMENT_BRANDS, GUITAR_FILTERS,
    PIANO_KEYS, PIANO_ACTION, DRUM_PIECES, DRUM_SHELL, STRING_SIZES
} from './brands/instruments';
import { TURKISH_CITIES } from './cities';
import {
    REAL_ESTATE_LISTING_TYPES, ROOM_COUNTS, BUILDING_AGES,
    FLOOR_NUMBERS, HEATING_TYPES, DEED_STATUS, ZONING_STATUS
} from './brands/realestate';
import {
    FURNITURE_MATERIALS, FURNITURE_STYLES, TEXTILE_FABRICS,
    APPLIANCE_ENERGY_CLASSES, BED_SIZES, KITCHENWIRE_MATERIALS
} from './brands/home';
import {
    OFFICE_DESK_TYPES, OFFICE_CHAIR_FEATURES, POWER_TOOL_TYPES,
    WORK_MACHINE_TYPES, CONSTRUCTION_PAINT_TYPES, GARDEN_TOOL_POWER
} from './brands/industrial';

export interface Category {
    id: string;
    label: string;
    icon: string;
    bgColor: string;
    iconColor: string;
    subCategories: SubCategory[];
    shippingConstraints?: {
        allowedSizes: ('xs' | 's' | 'm' | 'l' | 'xl')[];
        maxWeight?: number;
    };
    isBridgeOnly?: boolean;
}

export interface SubCategory {
    id: string;
    label: string;
    types: ProductType[];
    thumbnailLight?: string;
    thumbnailDark?: string;
    isBridgeOnly?: boolean;
}

export interface ProductType {
    id: string;
    label: string;
    attributes: AttributeDef[];
}

export interface AttributeDef {
    key: string;
    label: string;
    type: 'select' | 'text' | 'number' | 'checkbox';
    options?: string[];
    unit?: string;
    filterable?: boolean;
    searchable?: boolean;
    sortable?: boolean;
    range?: boolean;
    multiSelect?: boolean;
    required?: boolean;
    priority?: 'high' | 'medium' | 'low';
}

export const COMMON_ATTRIBUTES: AttributeDef[] = [
    {
        key: 'price',
        label: 'Fiyat',
        type: 'number',
        range: true,
        filterable: true,
        sortable: true,
        required: true,
        priority: 'high',
        unit: '₺'
    },
    {
        key: 'city',
        label: 'Şehir',
        type: 'select',
        options: TURKISH_CITIES,
        filterable: true,
        searchable: true,
        multiSelect: true,
        priority: 'high'
    },
    {
        key: 'condition',
        label: 'Ürün Durumu',
        type: 'select',
        options: ['Sıfır', 'Az Kullanılmış', 'İkinci El', 'Hasarlı', 'Kutu Açılmamış', 'Faturalı', 'Garantili'],
        filterable: true,
        required: true,
        priority: 'high'
    }
];

export const CATEGORIES: Category[] = [
    {
        id: 'electronics',
        label: 'Elektronik',
        icon: '📱',
        bgColor: 'bg-[#F2F4FF]',
        iconColor: 'text-[#5B7BFE]',
        shippingConstraints: {
            allowedSizes: ['s', 'm', 'l'],
            maxWeight: 20
        },
        subCategories: [
            {
                id: 'phones',
                label: 'Telefon',
                types: [
                    {
                        id: 'smartphones',
                        label: 'Cep Telefonu',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ELECTRONIC_BRANDS, filterable: true, searchable: true, multiSelect: true, required: true, priority: 'high' },
                            { key: 'model', label: 'Model', type: 'select', options: [], searchable: true, filterable: true, required: true, priority: 'high' },
                            { key: 'storage', label: 'Dahili Hafıza', type: 'select', options: ['64 GB', '128 GB', '256 GB', '512 GB', '1 TB'], filterable: true, sortable: true, priority: 'medium' },
                            { key: 'ram', label: 'RAM', type: 'select', options: ['4 GB', '6 GB', '8 GB', '12 GB', '16 GB', '24 GB'], filterable: true, priority: 'medium' },
                            { key: 'screen_type', label: 'Ekran Tipi', type: 'select', options: ['OLED', 'AMOLED', 'Super AMOLED', 'LCD', 'IPS LCD'], filterable: true, priority: 'low' },
                            { key: 'warranty', label: 'Garanti Durumu', type: 'select', options: ['Türkiye Garantili', 'İthalatçı Garantili', 'Garantisi Yok'], filterable: true, priority: 'low' }
                        ]
                    },
                    {
                        id: 'smartwatches',
                        label: 'Akıllı Saat & Bileklik',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: WATCH_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'model', label: 'Model', type: 'select', options: [], searchable: true, priority: 'high' },
                            { key: 'case_size', label: 'Kasa Boyutu', type: 'select', options: ['40mm', '41mm', '42mm', '44mm', '45mm', '47mm', '49mm'], filterable: true, priority: 'medium' },
                            { key: 'connectivity', label: 'Bağlantı', type: 'select', options: ['GPS', 'GPS + Cellular', 'Wi-Fi'], filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'tablets',
                label: 'Tablet',
                types: [
                    {
                        id: 'tablets_all',
                        label: 'Bütün Tablet Modelleri',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ELECTRONIC_BRANDS, filterable: true, searchable: true, multiSelect: true, required: true, priority: 'high' },
                            { key: 'model', label: 'Model', type: 'select', options: [], searchable: true, required: true, priority: 'medium' },
                            { key: 'storage', label: 'Hafıza', type: 'select', options: ['32 GB', '64 GB', '128 GB', '256 GB', '512 GB', '1 TB'], filterable: true, priority: 'medium' },
                            { key: 'connectivity', label: 'Bağlantı', type: 'select', options: ['Wi-Fi', 'Wi-Fi + Cellular'], filterable: true, priority: 'low' }
                        ]
                    }
                ]
            },
            {
                id: 'computers',
                label: 'Bilgisayar & Yazılım',
                types: [
                    {
                        id: 'laptops',
                        label: 'Dizüstü Bilgisayar (Laptop)',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: COMPUTER_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'cpu', label: 'İşlemci', type: 'select', options: ['Intel Core i3', 'Intel Core i5', 'Intel Core i7', 'Intel Core i9', 'AMD Ryzen 5', 'AMD Ryzen 7', 'AMD Ryzen 9', 'Apple M1', 'Apple M2', 'Apple M3', 'Apple M4'], filterable: true, priority: 'high' },
                            { key: 'ram', label: 'RAM', type: 'select', options: ['8 GB', '16 GB', '32 GB', '64 GB', '128 GB'], filterable: true, priority: 'medium' },
                            { key: 'ssd', label: 'SSD Kapasitesi', type: 'select', options: ['256 GB', '512 GB', '1 TB', '2 TB', '4 TB'], filterable: true, priority: 'medium' },
                            { key: 'gpu', label: 'Ekran Kartı', type: 'select', options: ['RTX 4050', 'RTX 4060', 'RTX 4070', 'RTX 4080', 'RTX 4090', 'GTX 1650', 'Entegre'], filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'pc_components',
                        label: 'Bilgisayar Bileşenleri',
                        attributes: [
                            { key: 'component_type', label: 'Bileşen Türü', type: 'select', options: ['İşlemci (CPU)', 'Anakart', 'Ekran Kartı (GPU)', 'RAM', 'SSD/HDD', 'Güç Kaynağı (PSU)', 'Kasa'], filterable: true, required: true, priority: 'high' },
                            { key: 'brand', label: 'Marka', type: 'select', options: [...COMPUTER_BRANDS, 'NVIDIA', 'AMD', 'Gigabyte', 'Corsair', 'Kingston'], filterable: true, required: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'printers',
                        label: 'Yazıcı & Tarayıcı',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: PRINTER_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'type', label: 'Tür', type: 'select', options: ['Lazer', 'Mürekkep Püskürtmeli', 'Tanklı', ' Çok Fonksiyonlu'], filterable: true, required: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'gaming',
                label: 'Oyun & Konsol',
                types: [
                    {
                        id: 'consoles',
                        label: 'Oyun Konsolu',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: GAMING_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'model', label: 'Model', type: 'select', options: [], searchable: true, required: true, priority: 'high' },
                            { key: 'storage', label: 'Depolama', type: 'select', options: ['500 GB', '825 GB', '1 TB', '2 TB'], filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'games',
                        label: 'Oyunlar',
                        attributes: [
                            { key: 'platform', label: 'Platform', type: 'select', options: ['PS5', 'PS4', 'Xbox Series X/S', 'PC', 'Nintendo Switch'], filterable: true, required: true, priority: 'high' },
                            { key: 'genre', label: 'Tür', type: 'select', options: ['Aksiyon', 'RPG', 'Spor', 'Yarış', 'FPS', 'Strateji'], filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'av_systems',
                label: 'Ses & Görüntü Sistemleri',
                types: [
                    {
                        id: 'tv_projection',
                        label: 'Televizyon & Projeksiyon',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: AV_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'type', label: 'Cihaz Tipi', type: 'select', options: ['Televizyon', 'Projeksiyon Cihazı'], filterable: true, required: true, priority: 'high' },
                            { key: 'resolution', label: 'Çözünürlük', type: 'select', options: ['4K UHD', '8K', 'Full HD (1080p)', 'HD (720p)'], filterable: true, required: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'audio',
                        label: 'Hoparlör & Kulaklık',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: AV_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'type', label: 'Tür', type: 'select', options: ['Kulak Üstü', 'Kulak İçi', 'Bluetooth Hoparlör', 'Soundbar', 'Hi-Fi'], filterable: true, required: true, priority: 'medium' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'vehicles',
        label: 'Vasıta',
        icon: '🚗',
        bgColor: 'bg-[#FFF1F2]',
        iconColor: 'text-[#F43F5E]',
        isBridgeOnly: true,
        subCategories: [
            {
                id: 'cars',
                label: 'Otomobil',
                types: [
                    {
                        id: 'sedan',
                        label: 'Sedan / Hatchback / SUV',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: VEHICLE_BRANDS, filterable: true, searchable: true, multiSelect: true, required: true, priority: 'high' },
                            { key: 'model', label: 'Model', type: 'select', options: [], searchable: true, filterable: true, required: true, priority: 'high' },
                            { key: 'year', label: 'Yıl', type: 'number', range: true, filterable: true, priority: 'high' },
                            { key: 'km', label: 'KM', type: 'number', filterable: true, sortable: true, range: true, required: true, priority: 'high' },
                            { key: 'fuel', label: 'Yakıt', type: 'select', options: ['Benzin', 'Dizel', 'LPG', 'Hibrit', 'Elektrik'], filterable: true, multiSelect: true, priority: 'medium' },
                            { key: 'gear', label: 'Vites', type: 'select', options: ['Manuel', 'Otomatik', 'Yarı Otomatik'], filterable: true, multiSelect: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'motorcycles',
                label: 'Motosiklet',
                types: [
                    {
                        id: 'moto_all',
                        label: 'Tüm Motosiklet Modelleri',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: MOTORCYCLE_BRANDS, filterable: true, required: true, priority: 'high' },
                            { key: 'cc', label: 'Motor Hacmi (cc)', type: 'select', options: ['50cc', '125cc', '250cc', '500cc', '600cc', '1000cc+'], filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            },
            {
                id: 'work_machines',
                label: 'İş Makineleri',
                types: [
                    {
                        id: 'excavator',
                        label: 'Ekskavatör',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Komatsu', 'Caterpillar', 'Volvo', 'Hitachi', 'JCB'], filterable: true, required: true, priority: 'high' },
                            { key: 'weight', label: 'Çalışma Ağırlığı (Ton)', type: 'number', filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'forklift',
                        label: 'Forklift',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Toyota', 'Linde', 'Jungheinrich', 'Hyster'], filterable: true, required: true, priority: 'high' },
                            { key: 'capacity', label: 'Taşıma Kapasitesi (Ton)', type: 'number', filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'generator',
                        label: 'Jeneratör',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Aksa', 'Teksan', 'Cummins', 'Pramac'], filterable: true, required: true, priority: 'high' },
                            { key: 'power', label: 'Güç (kVA)', type: 'number', filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'fashion',
        label: 'Moda & Giyim',
        icon: '👗',
        bgColor: 'bg-[#FFF2F9]',
        iconColor: 'text-[#FF69B4]',
        shippingConstraints: {
            allowedSizes: ['xs', 's', 'm'],
            maxWeight: 5
        },
        subCategories: [
            {
                id: 'womens_clothing',
                label: 'Kadın Giyim',
                types: [
                    {
                        id: 'w_upper',
                        label: 'Üst Giyim (Tişört, Gömlek, Bluz)',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: [...CLOTHING_SIZES_INT, ...CLOTHING_SIZES_NUM], filterable: true, required: true, priority: 'high' },
                            { key: 'material', label: 'Materyal', type: 'select', options: FASHION_MATERIALS, filterable: true, priority: 'medium' },
                            { key: 'pattern', label: 'Desen', type: 'select', options: FASHION_PATTERNS, filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'w_lower',
                        label: 'Alt Giyim (Pantolon, Etek, Şort)',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: CLOTHING_SIZES_NUM, filterable: true, required: true, priority: 'high' },
                            { key: 'material', label: 'Materyal', type: 'select', options: FASHION_MATERIALS, filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'w_dresses',
                        label: 'Elbise & Abiye',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: CLOTHING_SIZES_INT, filterable: true, required: true, priority: 'high' },
                            { key: 'style', label: 'Stil', type: 'select', options: FASHION_STYLES, filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'mens_clothing',
                label: 'Erkek Giyim',
                types: [
                    {
                        id: 'm_upper',
                        label: 'Üst Giyim',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: CLOTHING_SIZES_INT, filterable: true, required: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'm_lower',
                        label: 'Alt Giyim',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: CLOTHING_SIZES_NUM, filterable: true, required: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'm_suits',
                        label: 'Takım Elbise',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Beden', type: 'select', options: ['44', '46', '48', '50', '52', '54', '56', '58'], filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            },
            {
                id: 'shoes',
                label: 'Ayakkabı',
                types: [
                    {
                        id: 'w_shoes',
                        label: 'Kadın Ayakkabı',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Numara', type: 'select', options: SHOE_SIZES_EUR, filterable: true, required: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'm_shoes',
                        label: 'Erkek Ayakkabı',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'size', label: 'Numara', type: 'select', options: SHOE_SIZES_EUR, filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            },
            {
                id: 'bags_accessories',
                label: 'Çanta & Aksesuar',
                types: [
                    {
                        id: 'purses',
                        label: 'Çanta (Kadın & Erkek)',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: FASHION_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'type', label: 'Tür', type: 'select', options: ['Sırt Çantası', 'El Çantası', 'Omuz Çantası', 'Cüzdan', 'Valiz'], filterable: true, required: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'jewelry',
                        label: 'Takı & Mücevher',
                        attributes: [
                            { key: 'type', label: 'Tür', type: 'select', options: ['Kolye', 'Bileklik', 'Küpe', 'Yüzük', 'Set'], filterable: true, required: true, priority: 'high' },
                            { key: 'material', label: 'Materyal', type: 'select', options: ['Altın', 'Gümüş', 'Platin', 'Bujiteri'], filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'hobby',
        label: 'Hobi & Müzik',
        icon: '🎮',
        bgColor: 'bg-[#F5F3FF]',
        iconColor: 'text-[#8B5CF6]',
        subCategories: [
            {
                id: 'instruments',
                label: 'Müzik Aletleri',
                types: [
                    {
                        id: 'guitars',
                        label: 'Gitar',
                        attributes: [
                            { key: 'type', label: 'Tür', type: 'select', options: ['Klasik', 'Akustik', 'Elektro', 'Bas', 'Elektro-Akustik'], filterable: true, required: true, priority: 'high' },
                            { key: 'brand', label: 'Marka', type: 'select', options: [...INSTRUMENT_BRANDS.GUITAR.MAJOR, ...INSTRUMENT_BRANDS.GUITAR.TURKISH], searchable: true, filterable: true, required: true, priority: 'high' },
                            ...GUITAR_FILTERS as any
                        ]
                    },
                    {
                        id: 'pianos',
                        label: 'Piyano & Klavye',
                        attributes: [
                            { key: 'key_count', label: 'Tuş Sayısı', type: 'select', options: PIANO_KEYS, filterable: true, required: true, priority: 'high' },
                            { key: 'action', label: 'Tuş Hassasiyeti', type: 'select', options: PIANO_ACTION, filterable: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'drums',
                        label: 'Davul & Perküsyon',
                        attributes: [
                            { key: 'pieces', label: 'Parça Sayısı', type: 'select', options: DRUM_PIECES, filterable: true, required: true, priority: 'medium' },
                            { key: 'shell_material', label: 'Gövde Malzemesi', type: 'select', options: DRUM_SHELL, filterable: true, priority: 'low' }
                        ]
                    }
                ]
            },
            {
                id: 'antiques_collections',
                label: 'Antika & Koleksiyon',
                types: [
                    {
                        id: 'antiques_all',
                        label: 'Tüm Antika & Koleksiyonlar',
                        attributes: [
                            { key: 'type', label: 'Tür', type: 'select', options: ['Para', 'Pul', 'Antika Obje', 'Eski Belge', 'Madalya', 'Efmera'], filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'real_estate',
        label: 'Emlak',
        icon: '🏠',
        bgColor: 'bg-[#FFF7ED]',
        iconColor: 'text-[#EA580C]',
        isBridgeOnly: true,
        subCategories: [
            {
                id: 'residential',
                label: 'Konut',
                types: [
                    {
                        id: 'flat',
                        label: 'Daire',
                        attributes: [
                            { key: 'listing_type', label: 'İlan Tipi', type: 'select', options: REAL_ESTATE_LISTING_TYPES, filterable: true, required: true, priority: 'high' },
                            { key: 'room_count', label: 'Oda Sayısı', type: 'select', options: ROOM_COUNTS, filterable: true, required: true, priority: 'high' },
                            { key: 'net_area', label: 'Net Alan', type: 'number', unit: 'm²', filterable: true, range: true, priority: 'high' },
                            { key: 'building_age', label: 'Bina Yaşı', type: 'select', options: BUILDING_AGES, filterable: true, priority: 'medium' },
                            { key: 'heating', label: 'Isıtma', type: 'select', options: HEATING_TYPES, filterable: true, priority: 'medium' },
                            { key: 'floor', label: 'Bulunduğu Kat', type: 'select', options: FLOOR_NUMBERS, filterable: true, priority: 'medium' },
                            { key: 'furnished', label: 'Eşyalı', type: 'select', options: ['Evet', 'Hayır'], filterable: true, priority: 'low' },
                            { key: 'deed_status', label: 'Tapu Durumu', type: 'select', options: DEED_STATUS, filterable: true, priority: 'low' }
                        ]
                    },
                    {
                        id: 'villa',
                        label: 'Villa / Müstakil Ev',
                        attributes: [
                            { key: 'listing_type', label: 'İlan Tipi', type: 'select', options: REAL_ESTATE_LISTING_TYPES, filterable: true, required: true, priority: 'high' },
                            { key: 'room_count', label: 'Oda Sayısı', type: 'select', options: ROOM_COUNTS, filterable: true, required: true, priority: 'high' },
                            { key: 'land_area', label: 'Bahçe Alanı', type: 'number', unit: 'm²', filterable: true, range: true, priority: 'medium' },
                            { key: 'heating', label: 'Isıtma', type: 'select', options: HEATING_TYPES, filterable: true, priority: 'medium' },
                            { key: 'furnished', label: 'Eşyalı', type: 'select', options: ['Evet', 'Hayır'], filterable: true, priority: 'low' }
                        ]
                    },
                    {
                        id: 'residence',
                        label: 'Rezidans',
                        attributes: [
                            { key: 'listing_type', label: 'İlan Tipi', type: 'select', options: REAL_ESTATE_LISTING_TYPES, filterable: true, required: true, priority: 'high' },
                            { key: 'room_count', label: 'Oda Sayısı', type: 'select', options: ROOM_COUNTS, filterable: true, required: true, priority: 'high' },
                            { key: 'balcony', label: 'Balkon', type: 'select', options: ['Var', 'Yok'], filterable: true, priority: 'low' }
                        ]
                    }
                ]
            },
            {
                id: 'land',
                label: 'Arsa & Arazi',
                types: [
                    {
                        id: 'land_all',
                        label: 'Tüm Arsa & Arazi',
                        attributes: [
                            { key: 'listing_type', label: 'İlan Tipi', type: 'select', options: ['Satılık', 'Kiralık'], filterable: true, required: true, priority: 'high' },
                            { key: 'zoning', label: 'İmar Durumu', type: 'select', options: ZONING_STATUS, filterable: true, required: true, priority: 'high' },
                            { key: 'area', label: 'Toplam Alan', type: 'number', unit: 'm²', filterable: true, range: true, priority: 'high' },
                            { key: 'pafta_ada_parsel', label: 'Ada / Parsel', type: 'text', priority: 'low' },
                            { key: 'deed_status', label: 'Tapu Durumu', type: 'select', options: ['Müstakil Parsel', 'Hisseli', 'Zilliyet'], filterable: true, priority: 'low' }
                        ]
                    }
                ]
            },
            {
                id: 'commercial',
                label: 'İşyeri',
                types: [
                    {
                        id: 'commercial_all',
                        label: 'Tüm İşyeri Tipleri',
                        attributes: [
                            { key: 'listing_type', label: 'İlan Tipi', type: 'select', options: REAL_ESTATE_LISTING_TYPES, filterable: true, required: true, priority: 'high' },
                            { key: 'type', label: 'İşyeri Türü', type: 'select', options: ['Dükkan', 'Ofis', 'Depo', 'Showroom', 'Atölye', 'Fabrika', 'Plaza'], filterable: true, required: true, priority: 'high' },
                            { key: 'net_area', label: 'Net Alan', type: 'number', unit: 'm²', filterable: true, range: true, priority: 'high' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'home_life',
        label: 'Ev & Yaşam',
        icon: '🏠',
        bgColor: 'bg-[#F0FDFA]',
        iconColor: 'text-[#0D9488]',
        shippingConstraints: {
            allowedSizes: ['m', 'l', 'xl'],
            maxWeight: 100
        },
        subCategories: [
            {
                id: 'furniture',
                label: 'Mobilya',
                isBridgeOnly: true,
                types: [
                    {
                        id: 'sofa_set',
                        label: 'Koltuk Takımı',
                        attributes: [
                            { key: 'material', label: 'Materyal', type: 'select', options: FURNITURE_MATERIALS, filterable: true, required: true, priority: 'high' },
                            { key: 'style', label: 'Stil', type: 'select', options: FURNITURE_STYLES, filterable: true, required: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'dining_table',
                        label: 'Yemek Masası',
                        attributes: [
                            { key: 'material', label: 'Materyal', type: 'select', options: FURNITURE_MATERIALS, filterable: true, required: true, priority: 'high' },
                            { key: 'capacity', label: 'Kişi Sayısı', type: 'select', options: ['2', '4', '6', '8', '10+'], filterable: true, required: true, priority: 'medium' }
                        ]
                    },
                    {
                        id: 'bedroom_set',
                        label: 'Yatak Odası Takımı',
                        attributes: [
                            { key: 'material', label: 'Materyal', type: 'select', options: FURNITURE_MATERIALS, filterable: true, required: true, priority: 'high' },
                            { key: 'bed_size', label: 'Yatak Ölçüsü', type: 'select', options: BED_SIZES, filterable: true, required: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'home_appliances',
                label: 'Beyaz Eşya & Küçük Ev Aletleri',
                isBridgeOnly: true,
                types: [
                    {
                        id: 'appliances_all',
                        label: 'Tüm Beyaz Eşyalar',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: HOME_APPLIANCE_BRANDS, filterable: true, searchable: true, required: true, priority: 'high' },
                            { key: 'energy_class', label: 'Enerji Sınıfı', type: 'select', options: APPLIANCE_ENERGY_CLASSES, filterable: true, required: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'home_textile',
                label: 'Ev Tekstili',
                types: [
                    {
                        id: 'bedding',
                        label: 'Nevresim & Yatak Tekstili',
                        attributes: [
                            { key: 'material', label: 'Materyal', type: 'select', options: TEXTILE_FABRICS, filterable: true, required: true, priority: 'medium' },
                            { key: 'size', label: 'Ölçü', type: 'select', options: ['Tek Kişilik', 'Çift Kişilik', 'King Size'], filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            },
            {
                id: 'kitchenware',
                label: 'Mutfak & Yemek',
                types: [
                    {
                        id: 'cooking',
                        label: 'Pişirme Gereçleri (Tencere, Tava)',
                        attributes: [
                            { key: 'material', label: 'Materyal', type: 'select', options: KITCHENWIRE_MATERIALS, filterable: true, required: true, priority: 'medium' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'industrial_business',
        label: 'İş Dünyası & Sanayi',
        icon: '🏗️',
        bgColor: 'bg-[#F8FAFC]',
        iconColor: 'text-[#475569]',
        isBridgeOnly: true,
        subCategories: [
            {
                id: 'office_furniture',
                label: 'Ofis Mobilyası',
                types: [
                    {
                        id: 'office_desks',
                        label: 'Ofis Masaları',
                        attributes: [
                            { key: 'type', label: 'Masa Tipi', type: 'select', options: OFFICE_DESK_TYPES, filterable: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'office_chairs',
                        label: 'Ofis Koltukları',
                        attributes: [
                            { key: 'features', label: 'Özellikler', type: 'select', options: OFFICE_CHAIR_FEATURES, filterable: true, priority: 'medium' }
                        ]
                    }
                ]
            },
            {
                id: 'construction',
                label: 'İnşaat & Hırdavat',
                types: [
                    {
                        id: 'paint',
                        label: 'Boya & Malzeme',
                        attributes: [
                            { key: 'paint_type', label: 'Boya Türü', type: 'select', options: CONSTRUCTION_PAINT_TYPES, filterable: true, priority: 'high' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'baby_kids',
        label: 'Anne & Bebek',
        icon: '👶',
        bgColor: 'bg-[#FFF1F2]',
        iconColor: 'text-[#E11D48]',
        subCategories: [
            {
                id: 'baby_clothing',
                label: 'Bebek Giyim',
                types: [
                    {
                        id: 'baby_bodysuit',
                        label: 'Zıbın & Body',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Zara Kids', 'H&M', 'Carter\'s', 'LC Waikiki', 'Ebebek'], filterable: true, required: true, priority: 'high' },
                            { key: 'age_range', label: 'Yaş Aralığı', type: 'select', options: ['Yenidoğan', '0-3 Ay', '3-6 Ay', '6-12 Ay', '12-24 Ay'], filterable: true, required: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'baby_pants',
                        label: 'Pantolon & Tayt',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Zara Kids', 'H&M', 'Carter\'s', 'LC Waikiki', 'Ebebek'], filterable: true, required: true, priority: 'high' },
                            { key: 'age_range', label: 'Yaş Aralığı', type: 'select', options: ['Yenidoğan', '0-3 Ay', '3-6 Ay', '6-12 Ay', '12-24 Ay'], filterable: true, required: true, priority: 'high' }
                        ]
                    },
                    {
                        id: 'baby_outerwear',
                        label: 'Dış Giyim (Mont, Hırka)',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: ['Zara Kids', 'H&M', 'Carter\'s', 'LC Waikiki', 'Ebebek'], filterable: true, required: true, priority: 'high' },
                            { key: 'age_range', label: 'Yaş Aralığı', type: 'select', options: ['Yenidoğan', '0-3 Ay', '6-12 Ay', '12-24 Ay'], filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            },
            {
                id: 'baby_equipment',
                label: 'Bebek Gereçleri',
                types: [
                    {
                        id: 'strollers',
                        label: 'Bebek Arabaları & Pusetler',
                        attributes: [
                            { key: 'type', label: 'Tip', type: 'select', options: ['Travel Sistem', 'Baston', 'İkiz'], filterable: true, priority: 'high' }
                        ]
                    }
                ]
            }
        ]
    },
    {
        id: 'sports_outdoor',
        label: 'Spor & Outdoor',
        icon: '🎾',
        bgColor: 'bg-[#F0FDF4]',
        iconColor: 'text-[#16A34A]',
        subCategories: [
            {
                id: 'sports_gear',
                label: 'Spor Ekipmanları',
                types: [
                    {
                        id: 'fitness',
                        label: 'Fitness & Kondisyon',
                        attributes: [
                            { key: 'brand', label: 'Marka', type: 'select', options: SPORTS_BRANDS, filterable: true, required: true, priority: 'high' }
                        ]
                    }
                ]
            }
        ]
    }
];

export const getAttributesForCategory = (categoryId?: string, subCategoryId?: string, productTypeId?: string): AttributeDef[] => {
    if (!categoryId) return [];
    const category = CATEGORIES.find(c => c.id === categoryId);
    if (!category) return [];
    if (subCategoryId) {
        const subCategory = category.subCategories.find(s => s.id === subCategoryId);
        if (subCategory) {
            if (productTypeId) {
                const type = subCategory.types.find(t => t.id === productTypeId);
                if (type) return type.attributes;
            }
            return subCategory.types[0]?.attributes || [];
        }
    }
    return [];
};
